import React, { useMemo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import LoadingSpinner from "./../LoadingSpinner/LoadingSpinner";
import { FormatAsDollars } from "../../utils/Helpers/Format";
import { categorizeIncentiveForCatalog } from "../../utils/Helpers/IncentiveCategories";
import ZipChip from "../ZipChip/ZipChip";
import "./IncentiveCatalogCategories.scss";

const CARD_CTA_FALLBACK = "https://www.uber.com/us/en/drive/services/electric/zero-emissions-incentive/";

const parseIncentiveAmount = (incentive) => {
     const maxAmount = Number(incentive?.max_amount);

     if (!isNaN(maxAmount) && maxAmount > 0) {
          return maxAmount;
     }

     const typicalAmountText = String(incentive?.typical_amount || "");
     const amountMatches = typicalAmountText.match(/\d[\d,]*(?:\.\d+)?/g);

     if (amountMatches?.length) {
          return Math.max(...amountMatches.map((amount) => Number(amount.replace(/,/g, ""))));
     }

     const parsedPurchaseAmount = Number(incentive?.evaluation?.amount_in_purchase);

     if (!isNaN(parsedPurchaseAmount) && parsedPurchaseAmount > 0) {
          return parsedPurchaseAmount;
     }

     return 0;
};

const getPillLabel = (type) => {
     const normalizedType = String(type || "").toLowerCase();

     if (normalizedType.includes("rebate") || normalizedType.includes("grant")) return "Cash back";
     if (normalizedType.includes("tax")) return "Tax savings";
     if (normalizedType.includes("toll")) return "Toll perk";
     if (normalizedType.includes("financ") || normalizedType.includes("loan")) return "Financing";
     if (normalizedType.includes("discount")) return "Discount";
     if (normalizedType.includes("rate") || normalizedType.includes("electric")) return "Utility plan";
     if (normalizedType.includes("bill")) return "Bill credit";

     return type || "Program";
};

const PILL_LABEL_IDS = {
     "Cash back": "incentives.pill.cashBack",
     "Tax savings": "incentives.pill.taxSavings",
     "Toll perk": "incentives.pill.tollPerk",
     Financing: "incentives.pill.financing",
     Discount: "incentives.pill.discount",
     "Utility plan": "incentives.pill.utilityPlan",
     "Bill credit": "incentives.pill.billCredit"
};

const getPillIcon = (label) => {
     const props = {
          width: 14,
          height: 14,
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: 2.5,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          "aria-hidden": true
     };

     switch (label) {
          case "Cash back":
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="9" />
                         <path d="M12 7v10M9 9.5c0-1.1 1.34-2 3-2s3 .9 3 2-1.34 2-3 2-3 .9-3 2 1.34 2 3 2 3-.9 3-2" />
                    </svg>
               );

          case "Tax savings":
               return (
                    <svg {...props}>
                         <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                         <path d="M16 3v4M8 3v4M2 11h20" />
                    </svg>
               );

          case "Toll perk":
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="10" />
                         <path d="M8 12h8M12 8v8" />
                    </svg>
               );

          case "Financing":
               return (
                    <svg {...props}>
                         <path d="M2 17l10-10 4 4 6-6" />
                         <path d="M14 7h8v8" />
                    </svg>
               );

          case "Discount":
               return (
                    <svg {...props}>
                         <line x1="19" y1="5" x2="5" y2="19" />
                         <circle cx="6.5" cy="6.5" r="2.5" />
                         <circle cx="17.5" cy="17.5" r="2.5" />
                    </svg>
               );

          case "Utility plan":
               return (
                    <svg {...props}>
                         <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
                    </svg>
               );

          case "Bill credit":
               return (
                    <svg {...props}>
                         <rect x="2" y="4" width="20" height="16" rx="2" />
                         <path d="M2 10h20" />
                    </svg>
               );

          default:
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="10" />
                         <path d="M12 16v-4M12 8h.01" />
                    </svg>
               );
     }
};

const CATEGORIES = [
     { id: "new", titleId: "incentives.category.new", title: "New EV Incentives" },
     { id: "used", titleId: "incentives.category.used", title: "Used EV Incentives" },
     { id: "charging", titleId: "incentives.category.charging", title: "Charging Incentives" }
];

const buildIncentiveCategories = (incentives) => {
     const grouped = {
          new: [],
          used: [],
          charging: []
     };

     incentives.forEach((incentive) => {
          const incentiveCategories = categorizeIncentiveForCatalog(incentive);

          incentiveCategories.forEach((categoryId) => {
               grouped[categoryId].push(incentive);
          });
     });

     return CATEGORIES.map((category) => ({
          ...category,
          incentives: grouped[category.id].sort((a, b) => {
               const aAmount = parseIncentiveAmount(a);
               const bAmount = parseIncentiveAmount(b);

               if (aAmount !== bAmount) {
                    return bAmount - aAmount;
               }

               return String(a?.name || "").localeCompare(String(b?.name || ""));
          })
     })).filter((category) => category.incentives.length > 0);
};

const IncentiveRowCard = ({ incentive, useLocale }) => {
     const amount = parseIncentiveAmount(incentive);
     const amountText = amount > 0 ? FormatAsDollars(amount, useLocale) : incentive?.typical_amount || "N/A";
     const incentiveName = `${incentive?.name || ""}`;
     const typeLabel = getPillLabel(incentive?.type);

     return (
          <article className="inc-row-card">
               <span className="inc-row-accent" aria-hidden="true" />

               <div className="inc-row-left">
                    <span className="inc-row-pill">
                         {getPillIcon(typeLabel)}
                         <FormattedMessage
                              id={PILL_LABEL_IDS[typeLabel] || "incentives.pill.program"}
                              defaultMessage={typeLabel}
                         />
                    </span>

                    <h4 className="inc-row-title">{incentiveName}</h4>

                    {incentive?.description && <p className="inc-row-desc">{incentive.description}</p>}

                    <a
                         href={incentive?.details_url || CARD_CTA_FALLBACK}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inc-row-learn-btn">
                         <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <path d="M15 3h6v6" />
                              <path d="M10 14 21 3" />
                              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                         </svg>
                         <FormattedMessage id="incentives.learnMore" defaultMessage="Learn more" />
                         <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <path d="M5 12h14" />
                              <path d="m12 5 7 7-7 7" />
                         </svg>
                    </a>
               </div>

               {amountText !== "N/A" && (
                    <div className="inc-row-amount-col">
                         <div className="inc-row-amount">
                              <FormattedMessage id="incentives.upTo" defaultMessage="Up to" /> {amountText}
                         </div>
                    </div>
               )}

               <svg
                    className="inc-row-arrow"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
               </svg>
          </article>
     );
};

const IncentiveCategorySection = ({ category, isOpen, onToggle, useLocale }) => {
     const intl = useIntl();
     const headerOuterRef = useRef(null);
     const sentinelTopRef = useRef(null);
     const sentinelBottomRef = useRef(null);
     const [isStuck, setIsStuck] = useState(false);

     useEffect(() => {
          const headerEl = headerOuterRef.current;
          const sentinelTopEl = sentinelTopRef.current;
          const sentinelBottomEl = sentinelBottomRef.current;
          if (!headerEl || !sentinelTopEl || !sentinelBottomEl) return;

          const stuckRef = { current: false };
          const bottomVisibleRef = { current: true };

          const handleUpdate = () => {
               setIsStuck(stuckRef.current && bottomVisibleRef.current);
          };

          const topObserver = new window.IntersectionObserver(
               ([entry]) => {
                    stuckRef.current = entry.boundingClientRect.top < 0;
                    handleUpdate();
               },
               {
                    root: null,
                    threshold: 0,
                    rootMargin: "0px 0px 0px 0px"
               }
          );
          const bottomObserver = new window.IntersectionObserver(
               ([entry]) => {
                    bottomVisibleRef.current = entry.isIntersecting;
                    handleUpdate();
               },
               {
                    root: null,
                    threshold: 0
               }
          );
          topObserver.observe(sentinelTopEl);
          bottomObserver.observe(sentinelBottomEl);
          return () => {
               topObserver.disconnect();
               bottomObserver.disconnect();
          };
     }, []);

     return (
          <div className="inc-cat-section">
               <div className="inc-cat-sentinel" ref={sentinelTopRef} />

               <div className={`inc-cat-header-outer${isStuck ? " is-stuck" : ""}`} ref={headerOuterRef}>
                    <div
                         className="inc-cat-header"
                         role="button"
                         tabIndex={0}
                         aria-expanded={isOpen}
                         onClick={onToggle}
                         onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                   event.preventDefault();
                                   onToggle();
                              }
                         }}>
                         <h3 className="inc-cat-title">
                              {intl.formatMessage({ id: category.titleId, defaultMessage: category.title })}
                              <span className="inc-cat-count">({category.incentives.length})</span>
                         </h3>

                         <svg
                              className={`inc-cat-chevron ${isOpen ? "is-open" : ""}`}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <polyline points="6 9 12 15 18 9" />
                         </svg>
                    </div>
               </div>

               {isOpen && (
                    <div className="inc-cat-list">
                         {category.incentives.map((incentive) => (
                              <IncentiveRowCard
                                   key={incentive?.id || incentive?.name}
                                   incentive={incentive}
                                   useLocale={useLocale}
                              />
                         ))}
                    </div>
               )}
               <div ref={sentinelBottomRef} style={{ height: 1 }} />
          </div>
     );
};

const IncentiveCatalogCategories = ({ incentives, postcode, loading, onZipSubmit }) => {
     const intl = useIntl();

     const [openCategories, setOpenCategories] = useState({
          new: true,
          used: true,
          charging: true
     });
     const categories = useMemo(() => buildIncentiveCategories(incentives || []), [incentives]);

     if (!incentives) return <LoadingSpinner />;

     const allExpanded = categories.length > 0 && categories.every((category) => openCategories[category.id]);

     const handleToggleAll = () => {
          const nextOpenState = {};

          categories.forEach((category) => {
               nextOpenState[category.id] = !allExpanded;
          });

          setOpenCategories(nextOpenState);
     };

     const handleToggleCategory = (categoryId) => {
          setOpenCategories((currentState) => ({
               ...currentState,
               [categoryId]: !currentState[categoryId]
          }));
     };

     return (
          <div className="inc-categorized">
               <div className="inc-toolbar">
                    <div className="inc-toolbar-left">
                         {postcode && <ZipChip postcode={postcode} loading={loading} onZipChange={onZipSubmit} />}
                    </div>

                    <button type="button" className="inc-collapse-toggle" onClick={handleToggleAll}>
                         {allExpanded
                              ? intl.formatMessage({
                                     id: "incentives.collapseAll",
                                     defaultMessage: "Collapse all Categories"
                                })
                              : intl.formatMessage({
                                     id: "incentives.expandAll",
                                     defaultMessage: "Expand all Categories"
                                })}
                    </button>
               </div>

               {categories.map((category) => (
                    <IncentiveCategorySection
                         key={category.id}
                         category={category}
                         isOpen={!!openCategories[category.id]}
                         onToggle={() => handleToggleCategory(category.id)}
                         useLocale={intl.locale}
                    />
               ))}
          </div>
     );
};

export default IncentiveCatalogCategories;

IncentiveCatalogCategories.propTypes = {
     incentives: PropTypes.array,
     postcode: PropTypes.string,
     loading: PropTypes.bool,
     onZipSubmit: PropTypes.func
};

IncentiveCategorySection.propTypes = {
     category: PropTypes.object,
     isOpen: PropTypes.bool,
     onToggle: PropTypes.func,
     useLocale: PropTypes.string
};

IncentiveRowCard.propTypes = {
     incentive: PropTypes.object,
     useLocale: PropTypes.string
};
